import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress
} from "@mui/material";
import {
  ArrowBackIos as ArrowBackIosIcon,
  ErrorOutline as ErrorOutlineIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  MapsUgcOutlined as MapsUgcOutlinedIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../hooks/AuthContext";
import { ScreenshotButton } from "../ScreenshotButton";
import useAlert from "../../../hooks/useAlert";
import api from "../../../api";
import html2canvas from "html2canvas";
import { feedbackTypes } from '../index';
export function FeedbackContentStep({
  feedbackType,
  onFeedbackRestartRequested,
  onFeedbackSent,
}) {
  const theme = useTheme();
  const { user } = useAuth();
  const { showAlertSuccess, showAlertError } = useAlert();
  
  const [screenshot, setScreenshot] = useState(null);
  const [comment, setComment] = useState("");
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  const getTypeIcon = () => {
    switch (feedbackType.title) {
      case "Problema":
        return <ErrorOutlineIcon sx={{ color: feedbackTypes.BUG.color, fontSize: 28 }} />;
      case "Ideia":
        return <LightbulbOutlinedIcon sx={{ color: feedbackTypes.IDEA.color, fontSize: 28 }} />;
      case "Outro":
        return <MapsUgcOutlinedIcon sx={{ color: feedbackTypes.OTHER.color, fontSize: 28 }} />;
      default:
        return <HelpOutlineIcon sx={{ fontSize: 28 }} />;
    }
  };

  async function handleTakeScreenshot() {
    const canvas = await html2canvas(document.querySelector("html"));
    return canvas.toDataURL("image/png");
  }

  async function handleSubmitFeedback(event) {
    event.preventDefault();
    
    if (!comment.trim()) {
      showAlertError("Por favor, digite seu feedback antes de enviar.");
      return;
    }

    setIsSendingFeedback(true);
    
    try {
      let base64Anexo = screenshot;
      if (!screenshot) {
        base64Anexo = await handleTakeScreenshot();
      }

      const response = await api.post(`/feedback/SendFeedback`, {
        idCliente: user.id,
        tipoFeedback: feedbackType.title,
        comentario: comment,
        base64Print: base64Anexo,
      });

      if (response.data.errorMessage) {
        showAlertError(response.data.errorMessage);
      } else {
        showAlertSuccess("Feedback enviado com sucesso! Obrigado pela contribuição.");
        onFeedbackSent();
      }
    } catch (error) {
      showAlertError(error.message || "Erro ao enviar feedback. Tente novamente.");
    } finally {
      setIsSendingFeedback(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={onFeedbackRestartRequested}
          sx={{ mr: 1, color: theme.palette.text.secondary }}
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {getTypeIcon()}
          <Typography variant="h6" fontWeight="600" sx={{ ml: 1 }}>
            {feedbackType.title}
          </Typography>
        </Box>
        <Tooltip title="Iremos analisar seu Feedback e inserir em nossas melhorias na plataforma">
          <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Typography variant="body1" fontWeight="500" gutterBottom>
        Conte com detalhes o que está acontecendo
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Vamos tirar um print da tela para nos ajudar a identificar o feedback.
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={4}
        placeholder="Descreva com detalhes seu feedback, sugestão ou problema..."
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        sx={{ mb: 3 }}
      />

      {/* Screenshot and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <ScreenshotButton
          screenshot={screenshot}
          onScreenshotTaken={setScreenshot}
          isShowButton={true}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onFeedbackRestartRequested}
          disabled={isSendingFeedback}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitFeedback}
          disabled={isSendingFeedback || !comment.trim()}
          startIcon={isSendingFeedback ? <CircularProgress size={16} /> : null}
        >
          {isSendingFeedback ? "Enviando..." : "Enviar feedback"}
        </Button>
      </Box>
    </Box>
  );
}